import { useCallback, useRef, useState } from 'react';

/**
 * Comment of an order detail form, tracked against its persisted value. `hasCommentChange` drives
 * the save button and the leave guard, and `getPendingComment` is read when a transition modal is
 * confirmed: it returns the comment to carry with the transition, or `undefined` when it is
 * untouched — which spares the transition a useless save. Going through a ref keeps the transition
 * handlers — and the action menus built from them — stable across keystrokes.
 *
 * @param {Object} externalOrder - The order as the detail service shapes it
 *
 * @returns {Object} `{ comment, setComment, hasCommentChange, getPendingComment }`
 */
export const useOrderPendingComment = (externalOrder) => {
  const savedComment = externalOrder.comment ?? '';

  const [comment, setComment] = useState(savedComment);

  const commentRef = useRef(comment);

  commentRef.current = comment;

  const getPendingComment = useCallback(() => {
    if (commentRef.current === savedComment) {
      return undefined;
    }

    return commentRef.current;
  }, [savedComment]);

  return {
    comment,
    setComment,
    hasCommentChange: comment !== savedComment,
    getPendingComment,
  };
};
